/**
 * Plain-language copy for refused or failed access calls (SC-439). User text uses the regular
 * hyphen, never an em or en dash.
 */
import { getPermissionEntry } from '../../../types/rbac/catalogue'
import {
  fallbackFailureMessage,
  isRbacApiError,
  type RbacApiError,
  type RbacCatalogue,
  type RbacRefusalReason,
} from '../services/rbacService'

export const ASK_OWNER = 'Ask your workspace owner.'

/** The catalogue label when the API gave one, else the one compiled into this bundle. */
export function permissionLabel(key: string, catalogue?: RbacCatalogue | null): string {
  return (
    catalogue?.permissions.find((p) => p.key === key)?.label ??
    getPermissionEntry(key)?.label ??
    key
  )
}

export function needPermissionMessage(key: string, catalogue?: RbacCatalogue | null): string {
  return `You need ${permissionLabel(key, catalogue)} permission. ${ASK_OWNER}`
}

export function refusalMessage(
  reason: RbacRefusalReason | string | undefined,
  permission: string | undefined,
  catalogue?: RbacCatalogue | null
): string {
  switch (reason) {
    case 'target_holds_more':
      return permission
        ? `You can't change the access of someone who has ${permissionLabel(permission, catalogue)} permission, which you don't have.`
        : "You can't change the access of someone who has permissions you don't have."
    case 'self_edit':
      return "You can't change your own access. Ask another admin or the workspace owner."
    case 'owner_target':
      return "An Owner's access can't be changed here. A platform admin manages Owners."
    case 'not_held':
      return permission
        ? `You can't give someone ${permissionLabel(permission, catalogue)} permission, which you don't have.`
        : "You can't give someone permissions you don't have."
    case 'not_a_member':
      return "You're not a member of this workspace."
    default:
      // missing_permission, and any reason a newer server may send that this client doesn't know.
      return permission
        ? needPermissionMessage(permission, catalogue)
        : `You don't have permission to do that. ${ASK_OWNER}`
  }
}

/**
 * The explanation for a plain `fetch` that came back 403 with the server's permission refusal
 * (`{ error: 'forbidden', reason, permission }`), or undefined for any other response so the
 * caller keeps its own message.
 */
export function forbiddenText(
  status: number,
  body: { error?: unknown; reason?: string; permission?: string } | null | undefined,
  catalogue?: RbacCatalogue | null
): string | undefined {
  if (status !== 403 || body?.error !== 'forbidden') return undefined
  return refusalMessage(body.reason, body.permission, catalogue)
}

/** Stable copy for the owner-management codes, used when the server sent no sentence of its own. */
const OWNER_CODE_MESSAGES: Record<string, string> = {
  last_owner: 'A workspace must keep at least one active Owner. Make someone else an Owner first.',
  invalid_role: "That role can't be used. Pick another role of this workspace that isn't Owner.",
  member_not_found: "That person isn't an active member of this workspace.",
  owner_not_found: "That person isn't an Owner of this workspace.",
  company_not_found: "This workspace couldn't be found. It may have been deleted.",
  FORBIDDEN_PLATFORM_PERMISSION: 'Managing workspace Owners needs a platform administrator.',
  FORBIDDEN_GLOBAL_ADMIN_REQUIRED: 'Managing workspace Owners needs a platform administrator.',
}

const fillIn = (error: RbacApiError) =>
  error.code && error.message === fallbackFailureMessage(error.status)
    ? (OWNER_CODE_MESSAGES[error.code] ?? error.message)
    : error.message

/** What to show for any failure of an access call. */
export function describeAccessError(error: unknown, catalogue?: RbacCatalogue | null): string {
  if (isRbacApiError(error)) {
    // These 403s are not permission-key refusals, so they have no reason to map: the server's
    // sentence is the explanation.
    if (
      error.status === 403 &&
      error.code &&
      Object.prototype.hasOwnProperty.call(OWNER_CODE_MESSAGES, error.code)
    ) {
      return fillIn(error)
    }
    if (error.status === 403) return refusalMessage(error.reason, error.permission, catalogue)
    if (error.status === 401) return 'Your session has expired. Sign in again.'
    // The server's own sentence wins; the code only fills in when it sent none.
    return fillIn(error)
  }
  return 'Could not reach the server. Try again.'
}
